/**
 * Test Data pointer on the Tests page (#1262 slice 4 → reduced in
 * #1374 Slice 4).
 *
 * Rendered just below the `MultiSuiteChips` row on `TestDetail`, this
 * is a deliberately MINIMAL one-line pointer — not a management
 * surface. Two states only, matching `docs/mockups/test-data-banner-states.html`:
 *
 *   1. **Uses a Set** — the test is connected to a live Test Data Set.
 *      Shows "Uses <Set name>" and an "Open Test Data" action into the
 *      per-test data-settings page.
 *   2. **Not set up** — no Set yet (unbound, unscripted, or the Set was
 *      removed). Shows "Set up Test Data" into the Test Data catalog.
 *
 * Everything else — the reuse choice, recovering from a removed Set,
 * and reviewing changes after a regenerate — lives in the Test Data
 * screens (the settings page and the review-changes view), NOT here.
 * Slice 4 deliberately stripped the old auto-create / pending banners
 * and the rows/columns/reuse summary from this component so the Tests
 * page stays a pointer and the Test Data screens own management.
 *
 * Plain language: "Test Data" / "Uses" / "Set up Test Data" in every
 * operator-visible string. Code identifiers (`DataBindingDto`) stay.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Database } from 'lucide-react'
import {
  dataBindingsAPI,
  testDataSetsAPI,
  type DataBindingDto,
  type TestDataSetSummary,
} from '@/lib/api'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'

interface TestDataBannerProps {
  /** The persisted TestScript id. `undefined` when the test isn't
   * scripted yet — there's no binding to fetch, so we render the
   * "Not set up" pointer. */
  scriptId: string | undefined
  /** The TestCase id. Retained for call-site compatibility; no longer
   * used now that the per-test dismissal banners are gone. */
  testCaseId?: string
  /** Compact one-line control for the header's row-2 strip (#2257).
   * Same two states and same navigation as the full banner — just an
   * inline chip instead of a bordered row, and no leading icon so it
   * matches the other row-2 controls. */
  compact?: boolean
}

export function TestDataBanner({ scriptId, compact = false }: TestDataBannerProps) {
  const wp = useWorkspacePath()
  const [binding, setBinding] = useState<DataBindingDto | null>(null)
  const [setSummary, setSetSummary] = useState<TestDataSetSummary | null>(null)
  const [loaded, setLoaded] = useState(false)

  // Fetch the binding (+ the bound Set's name) when scriptId is known.
  // Reset derived state at the top of every effect so a fast scriptId
  // switch can't leave a stale Set name against the new test. Uses the
  // cheap catalog list for the Set's name rather than the full
  // GET /test-data-sets/:id (which serializes every row).
  useEffect(() => {
    setBinding(null)
    setSetSummary(null)
    if (!scriptId) {
      setLoaded(true)
      return
    }
    let cancelled = false
    setLoaded(false)
    void (async () => {
      try {
        const b = await dataBindingsAPI.getByScript(scriptId)
        if (cancelled) return
        setBinding(b)
        if (b && b.setId) {
          try {
            const list = await testDataSetsAPI.list()
            if (cancelled) return
            setSetSummary(list.sets.find((s) => s._id === b.setId) ?? null)
          } catch {
            /* catalog read failed — falls back to the "set up" pointer */
          }
        }
      } catch {
        if (!cancelled) setBinding(null)
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [scriptId])

  const catalogHref = wp('/test-data')
  const setHref = binding?.setId ? wp(`/test-data/${binding.setId}`) : catalogHref
  const settingsHref =
    binding && binding.setId
      ? wp(`/test-data/${binding.setId}/bindings/${binding.scriptId}`)
      : null

  // Until the fetch lands, render nothing — keeps the layout from
  // flashing a "set up" pointer before we know the bound state.
  if (!loaded) return null

  // "Uses a Set" is driven by the binding alone — a live, non-orphaned
  // binding with a setId. The Set's NAME (from the cheap catalog list)
  // is best-effort: a transient list failure, or a Set not in the
  // returned page, must NOT flip a genuinely-bound test to "Not set up"
  // (that would mislead the operator and send them down the wrong flow).
  // When the name is unknown we degrade the label, not the state. Only
  // an orphaned binding (its Set was deleted) falls into the set-up
  // state — recovery lives in the Test Data screens, not this pointer.
  const usesASet = Boolean(binding && binding.setId && !binding.wasOrphaned && settingsHref)

  // Row-2 compact control (#2257): one inline chip carrying the same
  // two states and links as the full banner below.
  if (compact) {
    return usesASet ? (
      <Link
        to={`${setHref}?test=${encodeURIComponent(binding!.scriptId)}`}
        className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted whitespace-nowrap"
        title={setSummary ? `Uses ${setSummary.name}` : 'Uses a Test Data Set'}
      >
        <span className="text-muted-foreground font-normal">Test Data</span>
        <span className="text-muted-foreground">·</span>
        <span className="truncate max-w-[10rem]">Uses {setSummary ? setSummary.name : 'a Set'}</span>
        <ChevronRight className="h-3 w-3 text-primary shrink-0" />
      </Link>
    ) : (
      <Link
        to={catalogHref}
        className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted whitespace-nowrap"
      >
        Set up Test Data
        <ChevronRight className="h-3 w-3 text-primary shrink-0" />
      </Link>
    )
  }

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 px-3 py-2 rounded border bg-card text-xs">
        <span className="flex items-center justify-center w-[18px] h-[18px] rounded bg-primary/10 text-primary flex-none">
          <Database className="h-3 w-3" />
        </span>
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
          Test Data
        </span>
        {usesASet ? (
          <>
            <span>
              Uses{' '}
              <Link to={setHref} className="font-medium text-primary hover:underline">
                {setSummary ? setSummary.name : 'a Test Data Set'}
              </Link>
            </span>
            <span className="flex-1" />
            {/* Opens the Set's data grid (fields + values) — the natural
             * "show me the data". The `?test=` carries which test you came
             * from so the grid can offer a direct "How this test uses its
             * data" action without the Tests-using-this-Set detour. */}
            <Link
              to={`${setHref}?test=${encodeURIComponent(binding!.scriptId)}`}
              className="rounded border px-2.5 py-1 font-medium text-foreground hover:bg-muted whitespace-nowrap"
            >
              Open Test Data
            </Link>
          </>
        ) : (
          <>
            <span className="text-muted-foreground">Not set up yet</span>
            <span className="flex-1" />
            <Link
              to={catalogHref}
              className="rounded border px-2.5 py-1 font-medium text-foreground hover:bg-muted whitespace-nowrap"
            >
              Set up Test Data
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
